import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone, { OwnProps } from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import NavigationModeMenu, {
    NavigationModeCookieName,
    NavigationModeMenuStyles,
} from "@insite/content-library/Components/NavigationModeMenu";
import { HeaderSignIn } from "@insite/content-library/Widgets/Common/HeaderSignIn";
import baseTheme from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentMode: getCookie(NavigationModeCookieName) || "Storefront",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Navigation = styled.div`
    color: ${getContrastColor("common.accent")};
    font-size: 15px;
    background-color: ${getColor("common.accent")};
    padding: 0 45px 0 45px;
    height: 50px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;
export interface LogoLinksSearchStyles {
    outerContainer: InjectableCss;
    container: InjectableCss;
    logoWrapper: InjectableCss;
    categoryNav: InjectableCss;
    searchMenuWrapper: InjectableCss;
    rightLinksWrapper: InjectableCss;
    signInSection: InjectableCss;
    locationSection: InjectableCss;
    cartAreaSection: InjectableCss;
}

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    outerContainer: {
        css: css`
            background-color: ${getColor("common.accent")};
        `,
    },
    container: {
        css: css`
            display: flex;
            width: 100%;
            margin: 0 auto;
            // XC Inner Maxwidth
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return css`
                    max-width: ${maxWidths[4]}px;
                `;
            }}

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-wrap: wrap;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    logoWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 2;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            img {
                max-height: 90px;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-height: 70px;
                                width: auto;
                                margin: 0;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        `,
    },
    categoryNav: {
        css: css`
            display: flex;
            align-items: center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            display: flex;
            align-items: center;
            flex-grow: 1;
            margin: 0 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 4;
                            flex-basis: 100%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    rightLinksWrapper: {
        css: css`
            margin-left: auto;
            text-align: right;
            display: flex;
            align-items: center;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 3;
                            margin-right: 10px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    signInSection: {
        css: css`
            display: flex;
            align-items: center;
            margin: 0 8px;
        `,
    },
    locationSection: {
        css: css`
            display: flex;
            align-items: center;
            margin: 0 8px;
        `,
    },
    cartAreaSection: {
        css: css`
            display: flex;
            align-items: center;
            margin: 0 8px;
        `,
    },
};

const LogoLinksSearch: React.FC<PageProps> = ({ id }) => (
    <StyledWrapper {...logoLinksSearchStyles.outerContainer}>
        <StyledWrapper {...logoLinksSearchStyles.container}>
            <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                <Zone contentId={id} zoneName="Logo" fixed />
            </StyledWrapper>
            <StyledWrapper {...logoLinksSearchStyles.categoryNav}>
                <Zone contentId={id} zoneName="CategoryNav" />
            </StyledWrapper>
            <StyledWrapper {...logoLinksSearchStyles.searchMenuWrapper}>
                <Zone contentId={id} zoneName="Search" fixed />
            </StyledWrapper>
            <StyledWrapper {...logoLinksSearchStyles.rightLinksWrapper}>
                <Hidden {...logoLinksSearchStyles.signInSection} className="link-container">
                    <Zone contentId={id} zoneName="SignIn" />
                </Hidden>
                <Hidden {...logoLinksSearchStyles.locationSection} className="link-container">
                    <Zone contentId={id} zoneName="LinkList" />
                </Hidden>
                <Hidden {...logoLinksSearchStyles.cartAreaSection} className="link-container">
                    <Zone contentId={id} zoneName="CartArea" />
                </Hidden>
            </StyledWrapper>
        </StyledWrapper>
    </StyledWrapper>
);

const logoLinksSearch: WidgetModule = {
    component: LogoLinksSearch,
    definition: {
        group: "Common",
        icon: "magnifying-glass",
    },
};

export default logoLinksSearch;
